import { PieceSymbol } from 'chess.js';
import { FEN, PieceType } from './chess'; // Assuming FEN type is in chess.ts

/**
 * Base Mission Interface
 * Contains properties common to all mission types.
 */
interface BaseMission {
  id: string;
  title: string;
  description: string;
  stage: number;
  difficulty: number;
  timeLimit: number | null;
  prerequisites: string[];
  briefing: string;
  objective: string;
  successCriteria: string;
  rewards: {
    experience: number;
    skillPoints: {
      coordinates: number;
      basicMoves: number;
      sequences: number;
      gameAnalysis: number;
    };
  };
  // For display and filtering purposes
  tags?: string[];
  // Shorthand for rewards.experience
  xpReward?: number;
  // Completion criteria
  completionCriteria?: { 
    maxMoves?: number;
    maxHints?: number;
    maxTime?: number;
  };
}

/**
 * Properties specific to Coordinate Missions
 */
export interface CoordinateMissionProps extends BaseMission {
  type: 'coordinate';
  // Add any coordinate-specific properties here, e.g.:
  numberOfCoordinates?: number;
}

/**
 * Properties specific to Move Missions
 */
export interface MoveMissionProps extends BaseMission {
  type: 'move';
  position: FEN; // Specific to move missions
  targetMove: string; // Specific to move mission
  pieces?: PieceSymbol[];
  focus?: 'attack' | 'defense' | 'control' | 'tactics' | 'strategy' | 'check';
}

/**
 * Properties specific to Sequence Missions
 * 
 * Sequence missions require the player to identify or execute a series of moves
 * that demonstrate a particular tactical theme or strategic concept.
 */
export interface SequenceMissionProps extends BaseMission {
  type: 'sequence';
  position: FEN;                // Starting position
  sequence: string[];           // Correct sequence of moves
  tacticalTheme: string;        // The main tactical theme (fork, pin, etc.)
  secondaryThemes?: string[];   // Optional additional themes present
  timePressure?: boolean;       // Whether time pressure is a factor
  // Success criteria specific to sequences
  sequenceCompletionCriteria?: {
    maxDeviation?: number;      // Max number of moves that can deviate from ideal sequence
    requiredThemeDetection?: boolean; // Whether player must identify the theme
  };
}

// Add interfaces for other mission types (Game) as needed
// export interface GameMissionProps extends BaseMission { type: 'game'; /* ... */ }

/**
 * Discriminated Union for any Mission type
 * Use this type when dealing with a mission of unknown specific type.
 */
export type Mission = CoordinateMissionProps | MoveMissionProps | SequenceMissionProps; // Updated to include SequenceMissionProps
// ^^ Renamed the main export to 'Mission' for broader use, was AnyMission

/**
 * Mission result type definition
 * 
 * Records the outcomes of completed missions
 */
export interface MissionResult {
  missionId: string;
  completed: boolean;
  score: number;
  accuracy: number;
  timeSpent: number;
  completedAt: string;
  // Optional fields for move-specific stats
  movesUsed?: number;
  hintsUsed?: number;
}

/**
 * Unified data structure for mission completion results passed 
 * from challenge components to the MissionPage/MoveMissionPage.
 */
export interface MissionCompletionData {
  success: boolean;
  score: number;
  accuracy: number;
  timeSpent: number;
  movesUsed?: number; // Optional: For move missions
  hintsUsed?: number; // Optional: For move missions
}

/**
 * Mission status type
 * 
 * Represents the current status of a mission in relation to the player's progress
 */
export type MissionStatus = 'locked' | 'available' | 'completed';

/**
 * Helper function to determine if a mission is available based on completed missions
 * 
 * @param mission The mission to check
 * @param completedMissions Array of completed mission IDs
 * @returns Whether the mission is available to play
 */
export const isMissionAvailable = (
  mission: Mission, 
  completedMissions: string[]
): boolean => {
  // A mission is available if all its prerequisites have been completed
  return mission.prerequisites.every(prereq => 
    completedMissions.includes(prereq)
  );
};

/**
 * Helper function to get the status of a mission
 * 
 * @param mission The mission to check
 * @param completedMissions Array of completed mission IDs
 * @returns The status of the mission
 */
export const getMissionStatus = (
  mission: Mission,
  completedMissions: string[]
): MissionStatus => {
  if (completedMissions.includes(mission.id)) {
    return 'completed';
  }
  
  if (isMissionAvailable(mission, completedMissions)) {
    return 'available';
  }
  
  return 'locked';
}; 